<script setup lang="ts">
import SpinnerLoader from '#components/elements/spinner/SpinnerLoader.vue';
import { useKpisPerimeter } from '#composables/kpisPerimeter';
import type { Toaster, ToasterProps } from '#composables/notifications/types/notification.type';
import { useTrackUserAppAccess } from '#domain/core/analytics/composables/useTrackUserAppAccess';
import { useProductFruits } from '#domain/core/user/composables/useProductFruits';
import { useProjectsGTM } from '#domain/projects/analytics/useProjectsGTM';
import { useAuthStore } from '#stores/auth.store';
import { useDataTableStore } from '#stores/dataTable.store';
import { useFiltersStore } from '#stores/filters.store';
import { useUserStore } from '#stores/user.store';
import { useConfigStore } from '#stores/config.store';
import { syncStoreWithLocalStorage } from '#stores/utils/syncStoreWithLocalStorage.util';
import { CToasters } from '@carrefour/design-system-components-vue3';
import { storeToRefs } from 'pinia';
import { onMounted, provide, ref, watch } from 'vue';
import { RouterView, useRouter } from 'vue-router';

const router = useRouter();
const authStore = useAuthStore();
const userStore = useUserStore();
const { initializeFilters } = useKpisPerimeter();

const toasters = ref<ToasterProps>([]);

useTrackUserAppAccess();
useProjectsGTM();

const { isAdmin, isImpersonating, authenticatedUser } = storeToRefs(userStore);
const { isInitialized: isAuthInitialized } = storeToRefs(authStore);

const addToaster = (props: Toaster) => {
  if (toasters.value) {
    toasters.value.push(props);
  }
};

onMounted(() => {
  authStore.initAuth();
});

// Stores user datatable configuration only for no-admin user (i.e. when not impersonating)
watch(
  [isAuthInitialized, isAdmin, isImpersonating],
  ([isAuthInitialized, isAdmin, isImpersonating]) => {
    if (isAuthInitialized && !isAdmin && !isImpersonating) {
      syncStoreWithLocalStorage<ReturnType<typeof useDataTableStore>>(useDataTableStore());
      syncStoreWithLocalStorage<ReturnType<typeof useFiltersStore>>(useFiltersStore());
      syncStoreWithLocalStorage<ReturnType<typeof useConfigStore>>(useConfigStore());
    }
  },
  {
    immediate: true
  }
);

// Fetch features when auth initialized and apply saved filters to current query if needed
watch(
  isAuthInitialized,
  async (isAuthInitialized) => {
    if (isAuthInitialized) {
      await router.isReady();
      initializeFilters();
    }
  },
  {
    immediate: true
  }
);

// Load libraries depending on feature toggle
watch(
  [authenticatedUser],
  ([authenticatedUser]) => {
    if (authenticatedUser) {
      useProductFruits(authenticatedUser);
    }
  },
  {
    immediate: true
  }
);

provide('addToaster', addToaster);
</script>

<template>
  <div v-if="!isAuthInitialized" class="h-screen w-screen flex items-center justify-center">
    <SpinnerLoader size="large" />
  </div>
  <RouterView v-else />
  <CToasters class="!max-w-md" :toasters="toasters" @hide="toasters = []" />
</template>

<style lang="postcss">
.c-toaster {
  @apply !max-w-md;
}
</style>
